import React from 'react';
import { FormatRupiah } from '@arismun/format-rupiah';

// Status -> label & warna badge.
const STATUS_META = {
  ok: { label: 'Siap', className: 'has-text-success', tag: 'is-success' },
  fix: { label: 'Perlu perbaikan', className: 'has-text-warning', tag: 'is-warning' },
  error: { label: 'Error', className: 'has-text-danger', tag: 'is-danger' },
  dup: { label: 'Duplikat', className: 'has-text-danger', tag: 'is-danger' },
};

/**
 * Daftar opsi kategori (dikelompokkan debit/kredit) untuk satu baris.
 * Dipakai bersama oleh tampilan tabel & kartu.
 */
const CategoryOptions = ({ row, debitCategories, kreditCategories }) => {
  const showDebit = row.type !== 'kredit';
  const showKredit = row.type !== 'debit';
  return (
    <>
      <option value="">— pilih —</option>
      {showDebit && debitCategories.length > 0 && (
        <optgroup label="Debit (masuk)">
          {debitCategories.map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </optgroup>
      )}
      {showKredit && kreditCategories.length > 0 && (
        <optgroup label="Kredit (keluar)">
          {kreditCategories.map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </optgroup>
      )}
    </>
  );
};

/**
 * Kartu preview untuk tampilan mobile (satu kartu per baris).
 */
const PreviewCard = ({ row, index, debitCategories, kreditCategories, pockets, onChange, onRemove }) => {
  const meta = STATUS_META[row.status] || STATUS_META.error;
  const isBad = row.status === 'error' || row.status === 'dup';

  return (
    <div className={`preview-card box mb-3 ${isBad ? 'preview-card-error' : ''}`}>
      <div className='preview-card-header'>
        <span className={`tag is-small ${meta.tag}`}>
          #{row.row} · {meta.label}
        </span>
        <div className='buttons are-small mb-0'>
          <button
            type='button'
            title='Hapus baris ini dari import'
            className='button is-small is-danger is-light'
            onClick={() => onRemove(index)}
          >
            <i className='fa fa-trash'></i>
            <span className='ml-1'>Hapus</span>
          </button>
        </div>
      </div>

      {row.unknownCategory && (
        <p className='is-size-7 has-text-grey mb-2'>
          Kategori CSV "{row.unknownCategory}" tidak dikenali — pilih manual.
        </p>
      )}

      {/* Jenis: radio button kiri (Debit) / kanan (Kredit) */}
      <div className='field mb-3'>
        <div className='preview-type-toggle'>
          <label className={`preview-type-option ${row.type === 'debit' ? 'is-active-debit' : ''}`}>
            <input
              type='radio'
              name={`type-${index}`}
              value='debit'
              checked={row.type === 'debit'}
              onChange={() => onChange(index, { type: 'debit' })}
            />
            <span>Debit (masuk)</span>
          </label>
          <label className={`preview-type-option ${row.type === 'kredit' ? 'is-active-kredit' : ''}`}>
            <input
              type='radio'
              name={`type-${index}`}
              value='kredit'
              checked={row.type === 'kredit'}
              onChange={() => onChange(index, { type: 'kredit' })}
            />
            <span>Kredit (keluar)</span>
          </label>
        </div>
      </div>

      <div className='field mb-3'>
        <label className='label is-small mb-1'>Jumlah</label>
        <input
          type='number'
          min='0'
          inputMode='numeric'
          className='input'
          value={row.amount === null || row.amount === undefined ? '' : row.amount}
          onChange={(e) => onChange(index, { amount: e.target.value })}
        />
        <p className='is-size-7 has-text-grey-light'>
          <FormatRupiah value={row.amount || 0} />
        </p>
      </div>

      <div className='field mb-3'>
        <label className='label is-small mb-1'>Keterangan</label>
        <input
          type='text'
          className='input'
          value={row.note || ''}
          onChange={(e) => onChange(index, { note: e.target.value })}
        />
      </div>

      <div className='field mb-3'>
        <label className='label is-small mb-1'>Kategori</label>
        <div className='select is-fullwidth'>
          <select
            value={row.categoryId || ''}
            onChange={(e) => onChange(index, { categoryId: e.target.value })}
          >
            <CategoryOptions
              row={row}
              debitCategories={debitCategories}
              kreditCategories={kreditCategories}
            />
          </select>
        </div>
      </div>

      <div className='field mb-3'>
        <label className='label is-small mb-1'>Dompet</label>
        <div className='select is-fullwidth'>
          <select
            value={row.pocketId || ''}
            onChange={(e) => onChange(index, { pocketId: e.target.value })}
          >
            <option value=''>(kosong)</option>
            {pockets.map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </div>
      </div>

      <div className='columns is-mobile mb-0'>
        <div className='column'>
          <label className='label is-small mb-1'>Tanggal</label>
          <input
            type='date'
            className='input'
            value={row.date || ''}
            onChange={(e) => onChange(index, { date: e.target.value })}
          />
        </div>
        <div className='column'>
          <label className='label is-small mb-1'>Waktu</label>
          <input
            type='time'
            className='input'
            value={row.time || ''}
            onChange={(e) => onChange(index, { time: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Tabel preview import. Setiap baris bisa diedit inline.
 *
 * @param {Array}    rows        daftar baris preview
 * @param {Array}    categories  daftar kategori book (masing2 punya id,title,type)
 * @param {Array}    pockets     daftar dompet book (id,title)
 * @param {Function} onChange    (index, patch) => void
 * @param {Function} onRemove    (index) => void
 */
const PreviewImportTable = ({ rows, categories, pockets, onChange, onRemove }) => {

  const debitCategories = categories.filter((c) => c.type === 'debit');
  const kreditCategories = categories.filter((c) => c.type === 'kredit');

  return (
    <div className='preview-wrapper'>
      {/* ===== Tampilan tabel (desktop/tablet >= 769px) ===== */}
      <div className='table-container preview-table-wrapper is-hidden-touch'>
        <table className='table is-fullwidth is-narrow preview-table'>
          <thead>
            <tr>
              <th>#</th>
              <th>Status</th>
              <th>Tanggal</th>
              <th>Waktu</th>
              <th>Jenis</th>
              <th>Jumlah</th>
              <th>Keterangan</th>
              <th>Kategori</th>
              <th>Dompet</th>
              <th className='has-text-centered'>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const meta = STATUS_META[row.status] || STATUS_META.error;
              const rowError = row.status === 'error' || row.status === 'dup';
              return (
                <tr key={index} className={rowError ? 'preview-row-error' : ''}>
                  <td className='has-text-grey is-size-7'>{row.row}</td>
                  <td>
                    <span className={`is-size-7 ${meta.className} has-text-weight-semibold`}>
                      {meta.label}
                    </span>
                    {row.unknownCategory && (
                      <p className='is-size-7 has-text-grey-light'>
                        CSV: "{row.unknownCategory}"
                      </p>
                    )}
                  </td>
                  <td>
                    <input
                      type='date'
                      className='input is-small preview-input'
                      value={row.date || ''}
                      onChange={(e) => onChange(index, { date: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type='time'
                      className='input is-small preview-input'
                      value={row.time || ''}
                      onChange={(e) => onChange(index, { time: e.target.value })}
                    />
                  </td>
                  <td>
                    <div className='select is-small'>
                      <select
                        value={row.type || ''}
                        onChange={(e) => onChange(index, { type: e.target.value })}
                      >
                        <option value=''>—</option>
                        <option value='debit'>Debit</option>
                        <option value='kredit'>Kredit</option>
                      </select>
                    </div>
                  </td>
                  <td>
                    <input
                      type='number'
                      min='0'
                      className='input is-small preview-input preview-amount'
                      value={row.amount === null || row.amount === undefined ? '' : row.amount}
                      onChange={(e) => onChange(index, { amount: e.target.value })}
                    />
                    <p className='is-size-7 has-text-grey-light'>
                      <FormatRupiah value={row.amount || 0} />
                    </p>
                  </td>
                  <td>
                    <input
                      type='text'
                      className='input is-small preview-input'
                      value={row.note || ''}
                      onChange={(e) => onChange(index, { note: e.target.value })}
                    />
                  </td>
                  <td>
                    <div className='select is-small is-fullwidth'>
                      <select
                        value={row.categoryId || ''}
                        onChange={(e) => onChange(index, { categoryId: e.target.value })}
                      >
                        <CategoryOptions
                          row={row}
                          debitCategories={debitCategories}
                          kreditCategories={kreditCategories}
                        />
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className='select is-small is-fullwidth'>
                      <select
                        value={row.pocketId || ''}
                        onChange={(e) => onChange(index, { pocketId: e.target.value })}
                      >
                        <option value=''>(kosong)</option>
                        {pockets.map((p) => (
                          <option key={p.id} value={p.id}>{p.title}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className='buttons are-small mb-0'>
                      <button
                        type='button'
                        title='Hapus baris ini dari import'
                        className='button is-small is-danger is-light'
                        onClick={() => onRemove(index)}
                      >
                        <i className='fa fa-trash'></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ===== Tampilan kartu (mobile < 769px) ===== */}
      <div className='is-hidden-desktop'>
        {rows.map((row, index) => (
          <PreviewCard
            key={index}
            row={row}
            index={index}
            debitCategories={debitCategories}
            kreditCategories={kreditCategories}
            pockets={pockets}
            onChange={onChange}
            onRemove={onRemove}
          />
        ))}
      </div>
    </div>
  );
};

export default PreviewImportTable;
